/* @moldura padrao */
/**
 * detalhe.tsx (autorizações) — Página dedicada: o pedido (formulário), a
 * decisão da aprovadora com campo privado, as referências comerciais e o
 * chat (com imagem). Tema AdminUX. Rota /autorizacoes/:id.
 *
 * REFEITO EM 01/09/2026 (ficha #26). O que ele apontou ao abrir um pedido
 * pendente: *"não fica muito claro o que é o quê"* — a loja não aparecia, a
 * descrição era um texto solto sem rótulo, e não havia onde quem pediu
 * explicar o que quer. Agora o pedido é um FORMULÁRIO com rótulo dentro do
 * campo (o padrão do app — memória `formulario-rotulo-flutuante`), o mesmo
 * arranjo da tela de criação:
 *
 *   - **pendente** e você é envolvido ou decide → os campos editam, e há um
 *     "Salvar" (`PUT /autorizacoes/{id}`);
 *   - **decidida** → os mesmos campos, travados (`disabled`): a ficha lê-se
 *     igual, e o que foi autorizado não se reescreve. Só as referências
 *     comerciais seguem editáveis, no cartão "Cliente, fornecedor e pedido", que é de
 *     onde se encerra.
 *
 * ─── EM ABAS DESDE 04/09/2026 ───────────────────────────────────────────────
 * Dele, ao ver tudo empilhado numa coluna só: *"isso tem que começar criando
 * aba dentro da área de autorização — uma aba para acompanhamento e uma aba
 * para conversa"*. São três naturezas diferentes de trabalho e não competem
 * pela mesma tela:
 *
 *   O pedido        o que se pede, a decisão e as referências comerciais
 *   Acompanhamento  o mapa das etapas — o que falta fazer depois do "sim"
 *   Conversa        a sala do motor único, ancorada nesta autorização
 *
 * A CONVERSA É O MOTOR ÚNICO desde 04/09/2026 (fatia 3a da ficha #10): o
 * cartão da direita é o `CustomMensagensDoItem` ancorado em
 * ('autorizacoes', id) — salas com participantes, menções, anexos, não lidas e
 * aviso pelo catálogo, o mesmo componente que a OS vai usar. O chat embutido
 * (`tbl_app_autorizacao_mensagens`) saiu da tela; as mensagens antigas ficam no
 * banco e NÃO foram migradas — decisão dele em 01/09, e em produção não havia
 * nenhuma (medido: 0 linhas no dump de 31/08; 3 no local, de teste).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Tab, Tabs } from 'react-bootstrap';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { formatarDataHora } from '@/lib/data';
import { doBanco, formatarDinheiro, paraBanco } from '@/lib/numero';
import { useAuth } from '@/auth/auth-context';
import {
  CustomCampoNumeroMoeda,
  CustomCampoNumeroPercentual,
} from '@/components/CustomCamposNumero';
import { CustomMensagensBadge, CustomMensagensDoItem, CustomMensagensPrevia } from '@/components/CustomMensagens';
import CustomSemAcesso from '@/components/CustomSemAcesso';
import { CustomDica } from '@/components/CustomDica';
import { CustomEtapas } from './CustomEtapas';
import type { Etapa } from './CustomEtapas';
import { numerosDasEtapas } from './CustomEtapasLista';
import { CustomAutorizacaoResumo } from './CustomAutorizacaoResumo';
import type {
  AutorizacaoDetalhe,
  LojaOpcao,

  TipoOpcao,
  UsuarioOpcao,
} from './shared';
import { ANCORA_OS, andamentoDaAutorizacao, badge, eDaOS, etapaAberta, rotuloStatus, varianteStatus } from './shared';
import { CustomCampoBusca, type ItemBusca } from '@/components/CustomCamposBusca';

/** O que a lista de OS devolve, do pouco que este campo usa. */
interface OSListaItem { id: number; codigo?: string | null; cliente_nome?: string | null; cidade?: string | null }
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { CustomAtividades } from './CustomAtividades';
import { CustomDocumentos } from './CustomDocumentos';

const soDigitos = (v: string) => v.replace(/\D/g, '');

const ABAS = new Set(['pedido', 'acompanhamento', 'atividade', 'documentos', 'conversa']);

export function AutorizacaoDetalhePage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { usuario, pode } = useAuth();
  /* ⛔ O `?voltar=` DA URL SAIU COM O BOTÃO PRÓPRIO. Era um destino cravado por
     quem abria esta tela — a mesma ideia do `voltarPara` da OS, que fazia o
     botão mentir para quem chegava por outro caminho. Agora o caminho de volta
     sai da trilha, na moldura, e vale para qualquer origem. */
  const podeAutorizar = Boolean(usuario?.autorizador || usuario?.admin);
  // O acompanhamento tem permissão própria: quem vê o pedido não vê as etapas.
  const etapasVisiveis = pode('autorizacoes.etapas.ver');
  const podeGerirEtapas = pode('autorizacoes.etapas.gerir');

  /* A aba vive na URL (`?aba=acompanhamento`). Sem isso, abrir a etapa e
     voltar remonta esta página no useState('pedido') — o defeito de 04/09. */
  const pedida = params.get('aba');
  const aba =
    pedida === 'acompanhamento' && !etapasVisiveis
      ? 'pedido'
      : pedida && ABAS.has(pedida)
        ? pedida
        : 'pedido';
  function setAba(k: string) {
    const next = new URLSearchParams(params);
    if (k === 'pedido') next.delete('aba');
    else next.set('aba', k);
    setParams(next, { replace: true });
  }
  // Só o NÚMERO, para o selo da aba — a lista mesma vive dentro do componente.
  const [etapasAbertas, setEtapasAbertas] = useState(0);
  /* Os números das etapas, para a conversa do caso etiquetar de onde cada
     mensagem veio. Sai da MESMA função do card — dois números para a mesma
     etapa seria pior que nenhum. */
  const [numerosPorEtapa, setNumerosPorEtapa] = useState<Record<number, string>>({});
  /* A lista inteira, para o painel lateral contar a história do caso. */
  const [etapasDoCaso, setEtapasDoCaso] = useState<Etapa[]>([]);
  const [aut, setAut] = useState<AutorizacaoDetalhe | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  /* O 403 do escopo de linha (Fase 3b) não é um erro de carregamento: é uma
     porta fechada de propósito, e tem tela própria. Guardar só a mensagem
     deixava os dois indistinguíveis. */
  const [semAcesso, setSemAcesso] = useState(false);

  // As listas do formulário — as mesmas da tela de criação.
  const [tipos, setTipos] = useState<TipoOpcao[]>([]);
  const [lojas, setLojas] = useState<LojaOpcao[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioOpcao[]>([]);

  // O pedido, como formulário (espelho do `aut` enquanto pendente)
  const [fLoja, setFLoja] = useState('');
  const [fTipo, setFTipo] = useState('');
  const [fCliente, setFCliente] = useState('');
  const [fFornecedor, setFFornecedor] = useState('');
  const [fOrcamento, setFOrcamento] = useState('');
  const [fPedido, setFPedido] = useState('');
  const [fOs, setFOs] = useState('');
  /* O ESCOLHIDO, para o campo de busca mostrar o que já está vinculado. `fOs`
     continua sendo o que vai para a API — os dois andam juntos no `onChange`. */
  const [osEscolhida, setOsEscolhida] = useState<ItemBusca | null>(null);

  /* Busca no SERVIDOR, dez por vez: é o que muda em relação ao select, que
     trazia todas as OS de uma vez. `limite` já existe na rota de OS. */
  async function buscarOS(termo: string): Promise<ItemBusca[]> {
    const achados = await api.get<OSListaItem[]>(
      `/ordens-servico?busca=${encodeURIComponent(termo)}&limite=10`,
    );
    return achados.map((o) => ({
      id: o.id,
      rotulo: o.codigo ?? `OS ${o.id}`,
      detalhe: [o.cliente_nome, o.cidade].filter(Boolean).join(' · ') || null,
    }));
  }
  const [fTitulo, setFTitulo] = useState('');
  const [fDescricao, setFDescricao] = useState('');
  const [fValor, setFValor] = useState('');
  const [fPercentual, setFPercentual] = useState('');
  const [fSolicitante, setFSolicitante] = useState('');
  const [fSolicitanteNome, setFSolicitanteNome] = useState('');
  const [sujo, setSujo] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // Decisão
  const [obsPublica, setObsPublica] = useState('');
  const [obsPrivada, setObsPrivada] = useState('');
  const [decidindo, setDecidindo] = useState(false);

  // Referências comerciais (078): o que segue editável DEPOIS da decisão.
  const [editandoRef, setEditandoRef] = useState(false);
  const [refCliente, setRefCliente] = useState('');
  const [refFornecedor, setRefFornecedor] = useState('');
  const [refOrcamento, setRefOrcamento] = useState('');
  const [refPedido, setRefPedido] = useState('');
  const [salvandoRef, setSalvandoRef] = useState(false);
  const [encerrando, setEncerrando] = useState(false);

  function espelhar(a: AutorizacaoDetalhe) {
    setFLoja(a.loja_id != null ? String(a.loja_id) : '');
    setFTipo(a.tipo_id != null ? String(a.tipo_id) : '');
    setFCliente(a.cliente_nome ?? '');
    setFFornecedor(a.fornecedor_nome ?? '');
    setFOrcamento(a.numero_orcamento != null ? String(a.numero_orcamento) : '');
    setFPedido(a.numero_pedido_venda != null ? String(a.numero_pedido_venda) : '');
    setFOs(eDaOS(a) && a.entidade_id ? String(a.entidade_id) : '');
    /* O campo de busca mostra o que JÁ está vinculado sem ir ao servidor: o
       rótulo da OS vem no próprio detalhe (`entidade_label`). */
    setOsEscolhida(
      eDaOS(a) && a.entidade_id
        ? { id: a.entidade_id, rotulo: a.entidade_label ?? `OS ${a.entidade_id}` }
        : null,
    );
    setFTitulo(a.titulo ?? '');
    setFDescricao(a.descricao ?? '');
    setFValor(doBanco(a.valor));
    setFPercentual(doBanco(a.percentual));
    setFSolicitante(a.solicitante_id != null ? String(a.solicitante_id) : '');
    setFSolicitanteNome(a.solicitante_id == null ? (a.solicitante_nome ?? '') : '');
    setSujo(false);
  }

  const carregarResumoEtapas = useCallback(async () => {
    try {
      const lista = await api.get<Etapa[]>(`/autorizacoes/${id}/etapas`);
      // `etapaAberta`, não `=== 'aberta'`: o número da aba parava de contar a
      // etapa assim que alguém a assumia — a etapa em andamento sumia da conta
      // justamente quando passava a ser a que mais importa.
      setEtapasAbertas(lista.filter(etapaAberta).length);
      setEtapasDoCaso(lista);
      setNumerosPorEtapa(
        Object.fromEntries(numerosDasEtapas(lista as never)) as Record<number, string>,
      );
    } catch {
      setEtapasAbertas(0); // sem permissão: a aba nem aparece
    }
  }, [id]);

  function carregar() {
    setCarregando(true);
    setErro(null);
    api
      .get<AutorizacaoDetalhe>(`/autorizacoes/${id}`)
      .then((a) => {
        setAut(a);
        espelhar(a);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 403) {
          setSemAcesso(true);
          return;
        }
        setErro(
          err instanceof ApiError
            ? err.message
            : 'Não foi possível carregar a autorização.',
        );
      })
      .finally(() => setCarregando(false));
  }

  useEffect(() => {
    carregar();
    api.get<TipoOpcao[]>('/autorizacoes/tipos').then(setTipos).catch(() => undefined);
    api.get<LojaOpcao[]>('/lojas').then(setLojas).catch(() => undefined);
    api.get<UsuarioOpcao[]>('/usuarios/opcoes').then(setUsuarios).catch(() => undefined);
    if (etapasVisiveis) carregarResumoEtapas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const tipoEscolhido = useMemo(
    () => tipos.find((t) => String(t.id) === fTipo) ?? null,
    [tipos, fTipo],
  );

  // Um `set` que também marca o formulário como mexido.
  const muda = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setSujo(true);
  };

  async function salvarPedido() {
    if (!aut) return;
    setSalvando(true);
    try {
      const atual = await api.put<AutorizacaoDetalhe>(`/autorizacoes/${id}`, {
        tipo_id: fTipo ? Number(fTipo) : null,
        loja_id: fLoja ? Number(fLoja) : null,
        titulo: fTitulo.trim(),
        descricao: fDescricao.trim() || null,
        cliente_nome: fCliente.trim() || null,
        fornecedor_nome: fFornecedor.trim() || null,
        numero_orcamento: fOrcamento ? Number(fOrcamento) : null,
        numero_pedido_venda: fPedido ? Number(fPedido) : null,
        valor: paraBanco(fValor),
        percentual: paraBanco(fPercentual),
        solicitante_id: fSolicitante ? Number(fSolicitante) : null,
        solicitante_nome: !fSolicitante && fSolicitanteNome.trim() ? fSolicitanteNome.trim() : null,
        entidade_tipo: fOs ? ANCORA_OS : null,
        entidade_id: fOs ? Number(fOs) : null,
      });
      setAut(atual);
      espelhar(atual);
      toast.success('Pedido atualizado.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  async function decidir(status: 'autorizada' | 'negada') {
    setDecidindo(true);
    try {
      const atual = await api.post<AutorizacaoDetalhe>(
        `/autorizacoes/${id}/decisao`,
        {
          status,
          obs_publica: obsPublica.trim() || null,
          obs_privada: obsPrivada.trim() || null,
          // O que está na tela: se o pedido mudou desde que abriu, o
          // servidor recusa (409) em vez de decidir sobre o valor errado.
          valor_visto: aut?.valor ?? null,
          percentual_visto: aut?.percentual ?? null,
        },
      );
      setAut(atual);
      espelhar(atual);
      setObsPublica('');
      setObsPrivada('');
      toast.success(status === 'autorizada' ? 'Autorizado.' : 'Negado.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível decidir.');
    } finally {
      setDecidindo(false);
    }
  }

  async function acao(rota: string, msg: string) {
    try {
      const atual = await api.post<AutorizacaoDetalhe>(`/autorizacoes/${id}/${rota}`);
      setAut(atual);
      espelhar(atual);
      toast.success(msg);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ação não concluída.');
    }
  }

  /* Rever a decisão pede o motivo, então é um passo, não um clique — o mesmo
     desenho do adiamento da etapa. */
  const [desistindo, setDesistindo] = useState(false);
  const [revendo, setRevendo] = useState(false);
  const [motivoRevisao, setMotivoRevisao] = useState('');
  const [revisando, setRevisando] = useState(false);

  async function rever() {
    if (!motivoRevisao.trim() || revisando) return;
    setRevisando(true);
    try {
      const atual = await api.post<AutorizacaoDetalhe>(
        `/autorizacoes/${id}/reabrir`, { motivo: motivoRevisao.trim() },
      );
      setAut(atual);
      espelhar(atual);
      setRevendo(false);
      toast.success('Registro corrigido — o pedido voltou para a decisão dela.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não deu para corrigir o registro.');
    } finally {
      setRevisando(false);
    }
  }

  function abrirEdicaoRef() {
    setRefCliente(aut?.cliente_nome ?? '');
    setRefFornecedor(aut?.fornecedor_nome ?? '');
    setRefOrcamento(aut?.numero_orcamento != null ? String(aut.numero_orcamento) : '');
    setRefPedido(aut?.numero_pedido_venda != null ? String(aut.numero_pedido_venda) : '');
    setEditandoRef(true);
  }

  async function salvarReferencias() {
    setSalvandoRef(true);
    try {
      const atual = await api.put<AutorizacaoDetalhe>(`/autorizacoes/${id}/referencias`, {
        cliente_nome: refCliente.trim() || null,
        fornecedor_nome: refFornecedor.trim() || null,
        numero_orcamento: refOrcamento ? Number(refOrcamento) : null,
        numero_pedido_venda: refPedido ? Number(refPedido) : null,
      });
      setAut(atual);
      espelhar(atual);
      setEditandoRef(false);
      toast.success('Referências atualizadas.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSalvandoRef(false);
    }
  }

  async function encerrar(virouVenda: boolean) {
    setEncerrando(true);
    try {
      const atual = await api.post<AutorizacaoDetalhe>(`/autorizacoes/${id}/encerrar`, {
        virou_venda: virouVenda,
      });
      setAut(atual);
      espelhar(atual);
      toast.success(virouVenda ? 'Encerrada — virou pedido de venda.' : 'Encerrada — não virou venda.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível encerrar.');
    } finally {
      setEncerrando(false);
    }
  }

  if (carregando) {
    return (
      <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
        <span className="spinner-border spinner-border-sm" /> Carregando…
      </div>
    );
  }
  if (semAcesso) {
    return (
      <CustomSemAcesso
        titulo="Esta autorização não é sua"
        detalhe="Você vê as que abriu, as pedidas em seu nome, as que decidiu e aquelas em que tem uma etapa. Quem acompanha a fila inteira é a gestão."
        voltarPara="/autorizacoes"
        voltarTexto="Ver as minhas"
      />
    );
  }
  if (erro || !aut) {
    return <div className="container py-5 text-center text-danger">{erro}</div>;
  }

  const valorPartes = [
    aut.valor != null ? formatarDinheiro(Number(aut.valor)) : null,
    aut.percentual != null ? `${Number(aut.percentual)}%` : null,
  ].filter(Boolean);
  // Cancelada e encerrada são os dois fins: nada mais muda, nem a conversa.
  // ⚠️ E são de EIXOS DIFERENTES desde a 092 — cancelada é decisão, encerrada é
  // andamento. Ler só o status deixava mexer numa autorização já fechada.
  const finalizada = aut.status === 'cancelada' || Boolean(aut.encerrada);
  const andamento = andamentoDaAutorizacao(aut);
  const envolvido =
    aut.registrante_id === usuario?.id || aut.solicitante_id === usuario?.id;
  const podeMexer = podeAutorizar || envolvido;
  /* ⛔ CANCELAR É DE QUEM PEDIU, e a tela ignorava isso. Até 11/09 aqui estava
     `!finalizada && podeMexer`, e `podeMexer` inclui `podeAutorizar` — então a
     ADRIANA via "Cancelar autorização" logo depois de ter autorizado, sem ter a
     permissão. Dele: *"esse botão só aparece para minha mãe? isso cancela todo o
     processo? é confuso"*. A permissão `autorizacoes.pedido.cancelar` está
     concedida a vendedor, instalador, sócia e gestor-administrativo — quem pede.
     Cancelar é "desisti, não quero mais", não é desfazer decisão: para isso existe
     `Corrigir o registro`, que é de administrador. */
  const podeCancelar = !finalizada && envolvido && pode('autorizacoes.pedido.cancelar');
  // O pedido inteiro só edita enquanto pendente — o mesmo guard da rota.
  const editavel = aut.status === 'pendente' && podeMexer;
  // Depois da decisão, só as referências comerciais.
  const podeEditarRef = !finalizada && !editavel && podeMexer;
  const pedeCliente = Boolean(tipoEscolhido?.exige_cliente || tipoEscolhido?.pede_pedido_venda);
  /* ⚠️ `fornecedor_nome` ENTRA NA CONDIÇÃO (099). Sem ele, um caso sem cliente —
     que é a maioria fora do Desconto — não mostrava este cartão, e o fornecedor
     ficava invisível assim que a autorização era decidida: gravado no banco e em
     lugar nenhum da tela. */
  const mostraReferencias = !editavel && Boolean(
    aut.cliente_nome || aut.fornecedor_nome || aut.numero_orcamento
    || aut.numero_pedido_venda || aut.tipo_pede_pedido_venda,
  );

  return (
      <CustomMolduraPagina
        /* ⛔ QUE PÁGINA É ESTA. Dele, 12/09/2026, usando: *"eu continuo sem
           saber se eu estou na tela de autorização ou não… talvez fosse sensato
           a gente preceder o título com a natureza daquela página"*.

           A tela da ETAPA já dizia ("Etapa da autorização · <caso>") e esta,
           que é a mãe, não dizia — então quem entrava pela lista via só o nome
           do pedido e tinha de deduzir pelas abas. O termo carrega o destaque;
           o nome do caso vem embaixo, no título, porque o caso a pessoa
           reconhece — o que ela não sabe é ONDE ESTÁ.

           Mesmo componente, mesma classe e mesmo peso da tela da etapa: são a
           mesma informação, e duas formas de dizê-la seriam duas verdades. */
        sobrescrito={
          <strong className="casaa-moldura-sobrescrito-termo">Autorização</strong>
        }
        titulo={aut.titulo}
        descricao={
          `${aut.tipo_nome ?? 'Genérica'}` +
          (aut.loja_nome ? ` · ${aut.loja_nome}` : '') +
          (valorPartes.length ? ` · ${valorPartes.join(' · ')}` : '')
        }
        caminho={[{ rotulo: 'Autorizações', para: '/autorizacoes' }]}
        ajuda="autorizacao-acompanhamento"
        acoes={
          /* OS DOIS SELOS (092): a DECISÃO e, ao lado, o ANDAMENTO. É o que
             responde de relance "posso parar de olhar isto?" — e o que faltava
             quando "Autorizada" parecia fim de linha. */
          <div className="d-flex gap-2 align-items-center">
            <span className={`badge ${badge(varianteStatus(aut.status))} fs-6`}>
              {rotuloStatus(aut.status)}
            </span>
            {andamento && (
              <span className={`badge ${andamento.cor} fs-6`}>{andamento.rotulo}</span>
            )}
          </div>
        }
      >

      {/* `adminuiux-tabs` é o padrão do app (a agenda usa assim): aba com o
          sublinhado do tema. Pílula não é o nosso — ele corrigiu em 04/09.
          `casaa-abas-rolam` porque CINCO abas não cabem em 390px: quebravam em
          duas linhas e a segunda deixava de parecer aba (10/09). */}
      <Tabs
        activeKey={aba}
        onSelect={(k) => k && setAba(k)}
        className="adminuiux-tabs casaa-abas-rolam mb-3"
      >
        <Tab eventKey="pedido" title="Autorização">
        <div className="row g-3">
        <div className="col-lg-7">
          {/* ── O PEDIDO, como formulário ─────────────────────────────────
              Os mesmos campos da tela de criação, na mesma ordem. Editáveis
              enquanto pendente; travados depois. */}
          <div className="card adminuiux-card mb-3">
            <div className="card-header bg-transparent d-flex align-items-center">
              <h6 className="mb-0">O pedido</h6>
              <span className="ms-auto small text-secondary">
                {aut.registrante_nome ?? '—'}
                {aut.criado_em ? ` · ${formatarDataHora(aut.criado_em)}` : ''}
              </span>
            </div>
            <div className="card-body">
              <fieldset disabled={!editavel}>
                <div className="row g-3">
                  <div className="col-sm-4">
                    <div className="form-floating">
                      <select
                        id="ped-loja"
                        className="form-select"
                        value={fLoja}
                        onChange={(e) => muda(setFLoja)(e.target.value)}
                      >
                        <option value="">—</option>
                        {lojas.map((l) => (
                          <option key={l.id} value={String(l.id)}>{l.nome_curto ?? l.nome}</option>
                        ))}
                      </select>
                      <label htmlFor="ped-loja">Loja</label>
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="form-floating">
                      <select
                        id="ped-tipo"
                        className="form-select"
                        value={fTipo}
                        onChange={(e) => muda(setFTipo)(e.target.value)}
                      >
                        <option value="">Genérica</option>
                        {tipos.map((t) => (
                          <option key={t.id} value={String(t.id)}>{t.nome}</option>
                        ))}
                      </select>
                      <label htmlFor="ped-tipo">Tipo</label>
                    </div>
                  </div>

                  {/* O FORNECEDOR VALE PARA TODO TIPO — ver o comentário no
                      `form.tsx`: o bloco do cliente só aparece no Desconto, e o
                      fornecedor importa justamente nos outros casos. */}
                  <div className="col-sm-4">
                    <div className="form-floating">
                      <input
                        id="ped-fornecedor"
                        className="form-control"
                        placeholder=" "
                        value={fFornecedor}
                        onChange={(e) => muda(setFFornecedor)(e.target.value)}
                      />
                      <label htmlFor="ped-fornecedor">Fornecedor</label>
                    </div>
                  </div>
                  {pedeCliente && (
                    <>
                      <div className="col-sm-4">
                        <div className="form-floating">
                          <input
                            id="ped-cliente"
                            className="form-control"
                            placeholder=" "
                            value={fCliente}
                            onChange={(e) => muda(setFCliente)(e.target.value)}
                          />
                          <label htmlFor="ped-cliente">Cliente *</label>
                        </div>
                      </div>
                      <div className="col-sm-4">
                        <div className="form-floating">
                          <input
                            id="ped-orcamento"
                            className="form-control"
                            inputMode="numeric"
                            placeholder=" "
                            value={fOrcamento}
                            onChange={(e) => muda(setFOrcamento)(soDigitos(e.target.value))}
                          />
                          <label htmlFor="ped-orcamento">Nº do orçamento</label>
                        </div>
                      </div>
                      <div className="col-sm-4">
                        <div className="form-floating">
                          <input
                            id="ped-pedido"
                            className="form-control"
                            inputMode="numeric"
                            placeholder=" "
                            value={fPedido}
                            onChange={(e) => muda(setFPedido)(soDigitos(e.target.value))}
                          />
                          <label htmlFor="ped-pedido">Nº do pedido de venda</label>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="col-12">
                    <div className="form-floating">
                      <input
                        id="ped-titulo"
                        className="form-control"
                        placeholder=" "
                        value={fTitulo}
                        onChange={(e) => muda(setFTitulo)(e.target.value)}
                      />
                      <label htmlFor="ped-titulo">Título *</label>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="form-floating">
                      <textarea
                        id="ped-descricao"
                        className="form-control"
                        style={{ height: 120 }}
                        placeholder=" "
                        value={fDescricao}
                        onChange={(e) => muda(setFDescricao)(e.target.value)}
                      />
                      <label htmlFor="ped-descricao">O que se pede, e por quê</label>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <CustomCampoNumeroMoeda
                      id="ped-valor"
                      label="Valor"
                      valor={fValor}
                      onChange={muda(setFValor)}
                      disabled={!editavel}
                    />
                  </div>
                  <div className="col-sm-6">
                    <CustomCampoNumeroPercentual
                      id="ped-percentual"
                      label="Percentual"
                      casas={2}
                      maximo={100}
                      valor={fPercentual}
                      onChange={muda(setFPercentual)}
                      disabled={!editavel}
                    />
                  </div>

                  <div className="col-sm-6">
                    <div className="form-floating">
                      <select
                        id="ped-solicitante"
                        className="form-select"
                        value={fSolicitante}
                        onChange={(e) => muda(setFSolicitante)(e.target.value)}
                      >
                        <option value="">
                          {fSolicitanteNome ? fSolicitanteNome : '— eu mesmo / não se aplica —'}
                        </option>
                        {usuarios.map((u) => (
                          <option key={u.id} value={String(u.id)}>{u.nome}</option>
                        ))}
                      </select>
                      <label htmlFor="ped-solicitante">Solicitante (em nome de quem)</label>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    {/* ⛔ NÃO É MAIS UM SELECT. Dele, 05/09: *"tem uma porrada de
                        OS, não dá — tem que ter um campo para você começar a
                        digitar e ela aparecer"*. A busca vai ao servidor; o
                        select carregava a lista inteira antes de a pessoa
                        digitar a primeira letra. */}
                    <CustomCampoBusca
                      id="ped-os"
                      label="Vinculada à OS"
                      valor={osEscolhida}
                      onChange={(item) => { setOsEscolhida(item); muda(setFOs)(item ? String(item.id) : ''); }}
                      buscar={buscarOS}
                      ajuda="Digite o número da OS ou o nome do cliente."
                    />
                  </div>
                </div>
              </fieldset>

              {editavel && (
                <div className="d-flex justify-content-end gap-2 mt-3">
                  {sujo && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      onClick={() => espelhar(aut)}
                      disabled={salvando}
                    >
                      Desfazer
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-theme btn-sm"
                    onClick={salvarPedido}
                    disabled={salvando || !sujo}
                  >
                    {salvando && <span className="spinner-border spinner-border-sm me-1" />}
                    Salvar alterações
                  </button>
                </div>
              )}
              {eDaOS(aut) && aut.entidade_id && (
                <div className="form-text mt-2">
                  <a href={`/ordens-servico/${aut.entidade_id}`}>Abrir a {aut.entidade_label ?? 'OS'}</a>
                </div>
              )}
            </div>
          </div>

          {/* ── Referências comerciais (078), depois da decisão ───────────
              É o ciclo do desconto: o pedido de venda chega DEPOIS do "sim".
              Por isso este cartão edita mesmo decidida — e é daqui que se
              encerra. Enquanto pendente, esses campos estão no formulário acima. */}
          {mostraReferencias && (
            <div className={`card adminuiux-card mb-3 ${aut.sem_pedido ? 'border-warning' : ''}`}>
              <div className="card-header bg-transparent d-flex align-items-center">
                <h6 className="mb-0">
                  <i className="bi bi-receipt me-2"></i>Cliente, fornecedor e pedido
                </h6>
                {podeEditarRef && !editandoRef && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link ms-auto p-0"
                    onClick={abrirEdicaoRef}
                  >
                    <i className="bi bi-pencil me-1"></i>Editar
                  </button>
                )}
              </div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-12">
                    <div className="form-floating">
                      <input
                        id="ref-cliente"
                        className="form-control"
                        placeholder=" "
                        value={editandoRef ? refCliente : (aut.cliente_nome ?? '')}
                        onChange={(e) => setRefCliente(e.target.value)}
                        disabled={!editandoRef}
                      />
                      <label htmlFor="ref-cliente">Cliente</label>
                    </div>
                  </div>
                  {/* O FORNECEDOR TAMBÉM SE DESCOBRE DEPOIS. No caso da PC, quem
                      soube de quem cobrar foi a Patrícia, semanas depois da
                      decisão — por isso ele entra no bloco que continua
                      editável com a autorização já decidida. */}
                  <div className="col-12">
                    <div className="form-floating">
                      <input
                        id="ref-fornecedor"
                        className="form-control"
                        placeholder=" "
                        value={editandoRef ? refFornecedor : (aut.fornecedor_nome ?? '')}
                        onChange={(e) => setRefFornecedor(e.target.value)}
                        disabled={!editandoRef}
                      />
                      <label htmlFor="ref-fornecedor">Fornecedor</label>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="form-floating">
                      <input
                        id="ref-orcamento"
                        className="form-control"
                        inputMode="numeric"
                        placeholder=" "
                        value={editandoRef ? refOrcamento : (aut.numero_orcamento != null ? String(aut.numero_orcamento) : '')}
                        onChange={(e) => setRefOrcamento(soDigitos(e.target.value))}
                        disabled={!editandoRef}
                      />
                      <label htmlFor="ref-orcamento">Nº do orçamento</label>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className={`form-floating ${aut.sem_pedido && !editandoRef ? 'border border-warning rounded' : ''}`}>
                      <input
                        id="ref-pedido"
                        className="form-control"
                        inputMode="numeric"
                        placeholder=" "
                        value={editandoRef ? refPedido : (aut.numero_pedido_venda != null ? String(aut.numero_pedido_venda) : '')}
                        onChange={(e) => setRefPedido(soDigitos(e.target.value))}
                        disabled={!editandoRef}
                      />
                      <label htmlFor="ref-pedido">
                        {aut.sem_pedido && !editandoRef ? 'Nº do pedido de venda — ainda sem número' : 'Nº do pedido de venda'}
                      </label>
                    </div>
                  </div>
                  {editandoRef && (
                    <div className="col-12 d-flex justify-content-end gap-2 mt-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-link"
                        onClick={() => setEditandoRef(false)}
                        disabled={salvandoRef}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-theme"
                        onClick={salvarReferencias}
                        disabled={salvandoRef}
                      >
                        {salvandoRef && <span className="spinner-border spinner-border-sm me-1" />}
                        Salvar
                      </button>
                    </div>
                  )}
                </div>

                {/* O encerramento: só a partir de autorizada, e só nos tipos
                    que pedem o pedido de venda (para os outros, autorizada já
                    é o fim). */}
                {aut.status === 'autorizada' && aut.tipo_pede_pedido_venda && podeEditarRef && !editandoRef && (
                  <div className="d-flex gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-theme flex-fill"
                      disabled={encerrando || aut.numero_pedido_venda == null}
                      title={aut.numero_pedido_venda == null ? 'Informe o número do pedido de venda antes' : undefined}
                      onClick={() => encerrar(true)}
                    >
                      <i className="bi bi-check2-all me-1"></i>Encerrar — virou pedido
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={encerrando}
                      onClick={() => encerrar(false)}
                    >
                      Não virou venda
                    </button>
                  </div>
                )}
                {aut.encerrada && (
                  <p className="mb-0 mt-2 small text-secondary">
                    Encerrada por <strong>{aut.usuario_encerramento_nome ?? '—'}</strong>
                    {aut.dt_encerramento ? ` em ${formatarDataHora(aut.dt_encerramento)}` : ''}
                    {' · '}
                    {aut.virou_venda ? 'virou pedido de venda' : 'não virou venda'}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Decisão pendente (só aprovador) */}
          {aut.status === 'pendente' && podeAutorizar && (
            <div className="card adminuiux-card mb-3 border-warning">
              <div className="card-header bg-transparent">
                <h6 className="mb-0">Decidir</h6>
              </div>
              <div className="card-body">
                <div className="form-floating mb-2">
                  <textarea
                    id="dec-publica"
                    className="form-control"
                    style={{ height: 70 }}
                    placeholder=" "
                    value={obsPublica}
                    onChange={(e) => setObsPublica(e.target.value)}
                  />
                  <label htmlFor="dec-publica">Observação (visível a todos)</label>
                </div>
                <div className="form-floating mb-3">
                  <textarea
                    id="dec-privada"
                    className="form-control"
                    style={{ height: 70 }}
                    placeholder=" "
                    value={obsPrivada}
                    onChange={(e) => setObsPrivada(e.target.value)}
                  />
                  <label htmlFor="dec-privada">
                    <i className="bi bi-lock me-1"></i>Anotação privada — por que autorizou, para lembrar depois
                  </label>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-success flex-fill"
                    disabled={decidindo}
                    onClick={() => decidir('autorizada')}
                  >
                    <i className="bi bi-check-lg me-1"></i> Autorizar
                  </button>
                  <button
                    className="btn btn-outline-danger flex-fill"
                    disabled={decidindo}
                    onClick={() => decidir('negada')}
                  >
                    <i className="bi bi-x-lg me-1"></i> Negar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Decisão já tomada. Desde a 092 a encerrada CONTINUA 'autorizada'
              — encerrar deixou de escrever por cima da decisão. */}
          {(aut.status === 'autorizada' || aut.status === 'negada') && (
            <div className="card adminuiux-card mb-3">
              <div className="card-body">
                <p className="mb-1 small text-secondary">
                  {aut.status === 'negada' ? 'Negado' : 'Autorizado'} por{' '}
                  <strong>{aut.aprovador_nome ?? '—'}</strong>
                  {aut.dt_decisao ? ` em ${formatarDataHora(aut.dt_decisao)}` : ''}
                </p>
                {aut.decisao_obs && (
                  <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                    {aut.decisao_obs}
                  </p>
                )}
                {aut.obs_privada && (
                  <div className="alert alert-warning py-2 px-3 mb-0 small">
                    <i className="bi bi-lock me-1"></i>
                    <strong>Anotação privada:</strong>{' '}
                    <span style={{ whiteSpace: 'pre-wrap' }}>{aut.obs_privada}</span>
                  </div>
                )}
                {/* REVER A DECISÃO — e vale para a autorizada também, desde
                    05/09: um "sim" dado por engano (valor errado, cliente
                    errado) não tinha volta pela tela. Encerrada não aparece
                    aqui: o ciclo fechou e o pedido de venda já existe lá fora.

                    O MOTIVO é obrigatório porque rever APAGA a decisão
                    anterior — depois disso ele é o único rastro de que ela
                    existiu. Mesma razão do adiamento da etapa. */}
                {/* ⛔ QUEM DECIDE NÃO DESFAZ A PRÓPRIA DECISÃO (11/09/2026).
                    Estava em `podeAutorizar`, então a ADRIANA revia o que ela
                    mesma tinha decidido. Dele: *"a autorização tem que ser
                    séria, até para os funcionários terem confiança no sistema;
                    não pode ser um negócio que você entra e altera toda hora"*.
                    Agora é de administrador — o desenho que ele descreveu:
                    *"ela vai poder falar comigo, Vittorio, corrige"*. */}
                {!aut.encerrada && usuario?.admin && (
                  revendo ? (
                    <div className="mt-3">
                      {/* O DIÁLOGO É ONDE O BOTÃO SE EXPLICA. Dele, 11/09: o
                          sistema é grande e as pessoas não lembram o que cada
                          coisa faz — então o passo diz o que vai acontecer, em
                          quê, e o que não se desfaz. */}
                      <p className="small text-secondary mb-2">
                        O pedido volta para <strong>pendente</strong> e a decisão
                        é devolvida a quem decide — ela decide de novo, e recebe
                        o aviso outra vez. O que estava registrado é apagado, e
                        <strong> este motivo fica à vista para sempre</strong>.
                        Isto é para o clique errado: se a decisão mudou porque o
                        caso mudou, converse no chat e encerre como “não virou
                        venda”.
                      </p>
                      <div className="form-floating mb-2">
                        <textarea
                          id="aut-motivo-revisao"
                          className="form-control"
                          style={{ height: '5rem' }}
                          value={motivoRevisao}
                          onChange={(ev) => setMotivoRevisao(ev.target.value)}
                          placeholder="O que estava errado no registro"
                        />
                        <label htmlFor="aut-motivo-revisao">
                          O que estava errado no registro
                        </label>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-theme"
                          disabled={!motivoRevisao.trim() || revisando}
                          onClick={rever}
                        >
                          {revisando
                            ? <span className="spinner-border spinner-border-sm me-1" />
                            : <i className="bi bi-arrow-counterclockwise me-1"></i>}
                          Corrigir e voltar para pendente
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setRevendo(false)}
                        >
                          Deixar como está
                        </button>
                      </div>
                    </div>
                  ) : (
                    <span className="d-inline-flex align-items-center gap-1 mt-2">
                      <button
                        className="btn btn-sm btn-outline-theme"
                        onClick={() => { setMotivoRevisao(''); setRevendo(true); }}
                      >
                        <i className="bi bi-eraser me-1"></i> Corrigir o registro
                      </button>
                      <CustomDica texto="Para o clique errado: devolve o pedido a quem decide, com o motivo escrito e à vista para sempre. Não serve para mudar de ideia — se o caso mudou, converse e encerre como “não virou venda”." />
                    </span>
                  )
                )}
              </div>
            </div>
          )}

          {/* ⛔ "CANCELAR AUTORIZAÇÃO" NÃO DIZIA NADA, e era pior que isso: ao
              lado de "Autorizar" e "Negar" lia-se como uma TERCEIRA decisão.
              Dele, 11/09: *"eu tenho autorizar e negar, cancelar autorização…
              eu mesmo não sei o que quer dizer"*.

              Não é decisão — é o solicitante RETIRANDO o pedido. Então o rótulo
              passa a dizer o gesto ("Desistir do pedido"), a dica explica antes,
              e o clique abre um passo que diz o que acontece e o que não volta
              (regra 8c da lei visual, escrita hoje por causa deste botão). */}
          {podeCancelar && (
            desistindo ? (
              <div className="mt-2 p-3 border rounded">
                <p className="small mb-2">
                  O pedido <strong>sai da fila de quem decide</strong> e não volta.
                  Use quando ele deixou de ser necessário — o cliente desistiu, o
                  valor mudou, o caso morreu.
                </p>
                <p className="small text-secondary mb-3">
                  Isto <strong>não é negar</strong>: negar é a resposta dela.
                  E não desfaz uma decisão já tomada.
                </p>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => { setDesistindo(false); acao('cancelar', 'Pedido retirado.'); }}
                  >
                    Desistir do pedido
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setDesistindo(false)}
                  >
                    Deixar como está
                  </button>
                </div>
              </div>
            ) : (
              <span className="d-inline-flex align-items-center gap-1">
                <button
                  className="btn btn-link text-danger btn-sm p-0"
                  onClick={() => setDesistindo(true)}
                >
                  Desistir do pedido
                </button>
                <CustomDica texto="Retira o pedido da fila de quem decide, sem virar recusa. Quem desiste é quem pediu — não desfaz uma decisão já tomada." />
              </span>
            )
          )}
        </div>

        {/* ── A LATERAL: A HISTÓRIA DO CASO ────────────────────────────────
            Dele, 05/09: *"o pedido continua ocupando metade da tela; monta um
            painel lateral que vai mostrando o próprio acompanhamento, uma
            timeline bem construidinha, para a pessoa entrar e já ver tudo"*.

            O espaço à direita estava VAZIO, e a informação que faltava ali é
            justamente a que obrigava a trocar de aba. */}
        <div className="col-lg-5 d-flex flex-column gap-3">
          {/* ⛔ O "COMO ESTÁ" TEM TETO, E O CHAT VEM LOGO ABAixo. Dele, 11/09:
              *"eu colocaria o chat abaixo do 'como está' e estabeleceria um
              tamanho máximo para o 'como está', e faria com que ele fosse
              rolável, sem aquele símbolozinho de scroll — a partir de um ponto
              vai ficar só o chat"*. Sem teto, uma autorização com sete etapas
              empurra a conversa para fora da tela, e a conversa é o que muda
              todo dia. A rolagem some (regra do app desde 28/08); o que avisa
              que há mais é o esmaecido no pé da lista. */}
          {etapasVisiveis && (
            <CustomAutorizacaoResumo
              aut={aut}
              etapas={etapasDoCaso}
              aoVerAcompanhamento={() => setAba('acompanhamento')}
            />
          )}
          <CustomMensagensPrevia
            ancora={{ tipo: 'autorizacoes', id: Number(id) }}
            aoAbrir={() => setAba('conversa')}
            /* SEM ISTO a etiqueta da mensagem cai em "De uma etapa" — genérica
               e inútil, que foi o que ele viu em 11/09. Com a tabela, ela diz
               `Etapa 2.1`, que é a pergunta que a etiqueta existe para
               responder (095). */
            numerosDasEtapas={numerosPorEtapa}
          />
        </div>
        </div>
        </Tab>

        {/* ── ACOMPANHAMENTO — o mapa das etapas ─────────────────────────────
            A aba só existe para quem tem `autorizacoes.etapas.ver`: as etapas
            são cegas para quem só enxerga o pedido. */}
        {etapasVisiveis && (
          <Tab
            eventKey="acompanhamento"
            title={
              <span>
                Acompanhamento
                {/* Na aba ATIVA o fundo já é azul, então o selo de destaque
                    sumiria dentro dele: aqui o contador é sempre claro sobre
                    escuro, ou o contrário, conforme a aba. */}
                {etapasAbertas > 0 && (
                  <span
                    className={`badge ms-2 ${aba === 'acompanhamento' ? 'text-bg-light' : 'casaa-selo-destaque'}`}
                  >
                    {etapasAbertas}
                  </span>
                )}
              </span>
            }
          >
            {/* SEM card em volta: cada etapa já É um card, e caixa dentro de
                caixa foi o que embolou a versão anterior. No fórum do tema os
                cards pousam direto sobre o fundo da página. */}
            <CustomEtapas
              autorizacaoId={Number(id)}
              usuarios={usuarios}
              podeGerir={podeGerirEtapas}
              aoMudar={carregarResumoEtapas}
            />
          </Tab>
        )}

        {/* A LINHA DO TEMPO DO CASO — o que foi feito, incluindo o que veio
            das etapas. É esta que responde "o que aconteceu aqui?" seis meses
            depois, e existe mesmo na autorização que nunca teve etapa. */}
        <Tab eventKey="atividade" title="Atividades">
          <div className="card adminuiux-card">
            <div className="card-body">
              <CustomAtividades autorizacaoId={Number(id)} podeEscrever />
            </div>
          </div>
        </Tab>

        {/* A COLEÇÃO DO CASO — o que entrou pelo pedido e o que entrou por
            qualquer etapa, junto. É a resposta à pergunta dele: *"o anexo é de
            uma etapa, mas ele também é da autorização"*. */}
        <Tab eventKey="documentos" title="Documentos">
          <div className="card adminuiux-card">
            <div className="card-body">
              <CustomDocumentos autorizacaoId={Number(id)} podeEscrever />
            </div>
          </div>
        </Tab>

        <Tab
          eventKey="conversa"
          title={
            /* A ABA DIZ QUE TEM COISA NOVA. Dele, 11/09: *"aquela aba tem que
               estar marcada; você tem que saber que recebeu uma mensagem"*. O
               número são as não lidas; o @ vermelho é menção — ver o badge. */
            <span className="d-inline-flex align-items-center">
              Chat
              <CustomMensagensBadge ancora={{ tipo: 'autorizacoes', id: Number(id) }} />
            </span>
          }
        >
        {/* ── A CONVERSA — motor único, ancorado nesta autorização ──────────
            `CustomMensagensDoItem` cuida de tudo: lista as salas do item, abre
            a primeira (quem tem `mensagens.sala.gerenciar`), e dentro dela vêm
            menção, anexo, não lidas e o aviso. Quem vê a autorização vê a sala
            de visibilidade "herdada" — a regra mora no contexto
            `autorizacoes` do `servico_mensagens_contextos.py`, não aqui. */}
        <div>
          <div className="card adminuiux-card">
            <div className="card-body">
              {/* ⛔ `abrirDireto` + `chatUnico` — as duas propriedades existem desde
                  04/09 e a PAGINA DA ETAPA passava as duas; esta aqui não passava
                  nenhuma, e por isso só nela apareciam o select "Quem vê" e o botão
                  "Iniciar chat" (que continuava lá DEPOIS de criado). Dele, 11/09:
                  *"não está claro o que é e o que não é… eu não estou escolhendo
                  nada"* — e não devia mesmo: é UM chat por autorização, decidido em
                  04/09. Quem vê a sala se LÊ dentro dela, não se escolhe aqui. */}
              <CustomMensagensDoItem
                ancora={{ tipo: 'autorizacoes', id: Number(id) }}
                tituloSugerido={aut.titulo}
                abrirDireto
                chatUnico
                numerosDasEtapas={numerosPorEtapa}
              />
            </div>
          </div>
        </div>
        </Tab>
      </Tabs>
    </CustomMolduraPagina>
  );
}
